// src/pages/MasterData.js
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Loader, AlertTriangle, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import Navbar from '../components/navbar';

function MasterData() {
  const navigate = useNavigate();
  const [selectedMaster, setSelectedMaster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [masterData, setMasterData] = useState([]);

  // PAGINATION
  const [currentPage, setCurrentPage] = useState(1);
  const [groupsPerPage] = useState(20);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0, hasMore: false });

  // API Base URL
  const API_BASE_URL = 'https://dpp-api.acespireconsulting.com/api';

  const fetchMasterData = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const offset = (page - 1) * groupsPerPage;

      const response = await fetch(`${API_BASE_URL}/product-groups?limit=${groupsPerPage}&offset=${offset}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch products: ${response.status}`);
      }

      const data = await response.json();
      
      console.log('API Response:', data);
      
      let groupsArray = [];
      let paginationData = null;

      if (data.success && data.data && data.data.groups) {
        groupsArray = data.data.groups;
        paginationData = data.data.pagination;
      } else if (data.groups) {
        groupsArray = data.groups;
        paginationData = data.pagination;
      }
      
      setMasterData(groupsArray);

      if (paginationData) {
        setPagination({
          total: paginationData.total || 0,
          totalPages: paginationData.totalPages || 1,
          hasMore: paginationData.hasMore || false
        });
      }

      window.scrollTo({ top: 0, behavior: 'smooth' });
      
    } catch (err) {
      console.error('Error fetching master data:', err);
      
      let errorMessage = err.message;
      
      if (err.message.includes('Failed to fetch') || err.message.includes('Network')) {
        errorMessage = 'Network error. Please check your internet connection or if the API server is running.';
      } else if (err.message.includes('CORS')) {
        errorMessage = 'CORS error. The API server may need to allow requests from this domain.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMasterData(currentPage);
  }, [currentPage]);

  // Pagination handlers
  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < pagination.totalPages) setCurrentPage(currentPage + 1);
  };

  const getShowingRange = () => {
    const start = (currentPage - 1) * groupsPerPage + 1;
    const end = Math.min(currentPage * groupsPerPage, pagination.total);
    return { start, end };
  };

  const handleSelectMaster = (id) => {
    setSelectedMaster(prev => 
      prev.includes(id) 
        ? prev.filter(masterId => masterId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedMaster.length === masterData.length) {
      setSelectedMaster([]);
    } else {
      setSelectedMaster(masterData.map(m => m.id));
    }
  };

  const handleRetry = () => {
    setError(null);
    setCurrentPage(1);
    fetchMasterData(1);
  };

  const handleViewProducts = (size) => {
    navigate(`/product-data?size=${size}`);
  };

  // Pagination Controls Component
  const PaginationControls = () => {
    const { start, end } = getShowingRange();
    return (
      <div className="bg-white border-t border-gray-200 px-4 py-3 sm:px-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-sm text-gray-700">
            Showing <span className="font-medium">{start}</span> to <span className="font-medium">{end}</span> of{' '}
            <span className="font-medium">{pagination.total}</span> product groups
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className={`px-3 py-1.5 rounded-lg flex items-center gap-1 text-sm font-medium transition-colors ${
                currentPage === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
            <div className="px-3 py-1.5 text-sm font-medium text-gray-700">
              Page {currentPage} of {pagination.totalPages}
            </div>
            <button
              onClick={handleNextPage}
              disabled={currentPage === pagination.totalPages}
              className={`px-3 py-1.5 rounded-lg flex items-center gap-1 text-sm font-medium transition-colors ${
                currentPage === pagination.totalPages
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Product Card Component for Mobile
  const MasterCard = ({ master }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3">
          <input 
            type="checkbox" 
            checked={selectedMaster.includes(master.id)}
            onChange={() => handleSelectMaster(master.id)}
            className="w-5 h-5 mt-1"
          />
          <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center border border-gray-300">
            <svg className="w-10 h-10 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="8" y1="21" x2="16" y2="21" strokeWidth="2" strokeLinecap="round"/>
              <line x1="12" y1="17" x2="12" y2="21" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
        </div>
        <button 
          onClick={() => handleViewProducts(master.size)}
          className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 active:bg-blue-800 transition-colors flex items-center gap-1"
        >
          <Eye className="w-3 h-3" />
          View Products
        </button>
      </div>

      <div className="space-y-2">
        <div>
          <p className="text-xs text-gray-500 mb-0.5">Product Group</p>
          <p className="text-sm font-medium text-gray-900">{master.productName}</p>
        </div>
        
        <div className="grid grid-cols-2 gap-2">
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Brand</p>
            <p className="text-sm text-gray-700">{master.brand}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Category</p>
            <p className="text-sm text-gray-700">{master.category}</p>
          </div>
        </div>

        <div>
          <p className="text-xs text-gray-500 mb-0.5">Products Count</p>
          <p className="text-sm text-gray-700">{master.productsCount} Products</p>
        </div>
      </div>
    </div>
  );

  if (loading && currentPage === 1) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#EDEDED' }}>
        <Navbar/>
        
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading master data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#EDEDED' }}>
        <Navbar/>
        
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center max-w-md mx-auto p-6">
            <AlertTriangle className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Master Data</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={handleRetry}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 mx-auto"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#EDEDED' }}>
      <Navbar/>

      {/* Loading overlay for page changes */}
      {loading && currentPage > 1 && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center gap-3">
            <Loader className="w-6 h-6 text-blue-600 animate-spin" />
            <p className="text-gray-700">Loading page {currentPage}...</p>
          </div>
        </div>
      )}

      {/* Back and Title Section */}
      <div className="bg-white px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 mx-2 sm:mx-4 mt-2 sm:mt-4 rounded-xl overflow-hidden shadow">
        <button 
          onClick={() => navigate('/dashboard')}
          className="flex items-center text-blue-600 hover:text-blue-700 transition-colors text-sm sm:text-base"
        >
          <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <h2 className="text-blue-600 font-medium text-sm sm:text-base">
          Product Groups ({pagination.total} groups)
        </h2>
      </div>

      {/* Select All Section - Mobile Only (for Card View) */}
      <div className="md:hidden mx-2 sm:mx-4 mt-4 bg-white rounded-lg shadow-sm p-4">
        <label className="flex items-center gap-3 cursor-pointer">
          <input 
            type="checkbox" 
            checked={selectedMaster.length === masterData.length && masterData.length > 0}
            onChange={handleSelectAll}
            className="w-5 h-5"
          />
          <span className="text-sm font-medium text-gray-700">
            Select All ({selectedMaster.length}/{masterData.length} selected)
          </span>
        </label>
      </div>

      {/* Content with Gray Background - Responsive */}
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Desktop Table View */}
        <div className="hidden lg:block bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-blue-100">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input 
                      type="checkbox" 
                      checked={selectedMaster.length === masterData.length && masterData.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Product Img</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Product Group</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Brand</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Category</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Products Count</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Action</th>
                </tr>
              </thead>
              <tbody>
                {masterData.map((master, index) => (
                  <tr key={master.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-4">
                      <input 
                        type="checkbox" 
                        checked={selectedMaster.includes(master.id)}
                        onChange={() => handleSelectMaster(master.id)}
                        className="w-4 h-4"
                      />
                    </td>
                    <td className="px-4 py-4">
                      <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center border border-gray-300">
                        <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <rect x="2" y="3" width="20" height="14" rx="2" ry="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <line x1="8" y1="21" x2="16" y2="21" strokeWidth="2" strokeLinecap="round"/>
                          <line x1="12" y1="17" x2="12" y2="21" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700">{master.productName}</td>
                    <td className="px-4 py-4 text-sm text-gray-700">{master.brand}</td>
                    <td className="px-4 py-4 text-sm text-gray-700">{master.category}</td>
                    <td className="px-4 py-4 text-sm text-gray-700">{master.productsCount}</td>
                    <td className="px-4 py-4">
                      <button 
                        onClick={() => handleViewProducts(master.size)} 
                        className="px-4 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                      >
                        View Products
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pagination.totalPages > 1 && <PaginationControls />}
        </div>

        {/* Tablet Horizontal Scroll Table */}
        <div className="hidden md:block lg:hidden bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead className="bg-blue-100">
                <tr>
                  <th className="px-3 py-2 text-left">
                    <input 
                      type="checkbox" 
                      checked={selectedMaster.length === masterData.length && masterData.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4"
                    />
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Img</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Product</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Brand</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Category</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Count</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Action</th>
                </tr>
              </thead>
              <tbody>
                {masterData.map((master, index) => (
                  <tr key={master.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-3 py-3">
                      <input 
                        type="checkbox" 
                        checked={selectedMaster.includes(master.id)}
                        onChange={() => handleSelectMaster(master.id)}
                        className="w-4 h-4"
                      />
                    </td>
                    <td className="px-3 py-3">
                      <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center border border-gray-300">
                        <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <rect x="2" y="3" width="20" height="14" rx="2" ry="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <line x1="8" y1="21" x2="16" y2="21" strokeWidth="2" strokeLinecap="round"/>
                          <line x1="12" y1="17" x2="12" y2="21" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-700">{master.productName}</td>
                    <td className="px-3 py-3 text-xs text-gray-700">{master.brand}</td>
                    <td className="px-3 py-3 text-xs text-gray-700">{master.category}</td>
                    <td className="px-3 py-3 text-xs text-gray-700">{master.productsCount}</td>
                    <td className="px-3 py-3">
                      <button 
                        onClick={() => handleViewProducts(master.size)}
                        className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors whitespace-nowrap"
                      >
                        View Products
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 text-center border-t">
            Scroll horizontally to see more columns →
          </div>
          {pagination.totalPages > 1 && <PaginationControls />}
        </div>

        {/* Mobile Card View - ONLY CARDS */}
        <div className="md:hidden">
          {masterData.map(master => (
            <MasterCard key={master.id} master={master} />
          ))}
          
          {pagination.totalPages > 1 && (
            <div className="bg-white rounded-lg shadow-sm mt-4">
              <PaginationControls />
            </div>
          )}
        </div>

        {/* Empty State */}
        {masterData.length === 0 && !loading && (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No master data found</h3>
            <p className="text-gray-600">There are currently no products in the system.</p>
          </div>
        )}

        {/* Selected Count - Mobile Footer */}
        {selectedMaster.length > 0 && (
          <div className="md:hidden fixed bottom-0 left-0 right-0 bg-blue-600 text-white p-4 shadow-lg z-40">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                {selectedMaster.length} item{selectedMaster.length !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={() => setSelectedMaster([])}
                className="text-sm underline hover:no-underline"
              >
                Clear Selection
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MasterData;